<template>
  <div>
    <v-container v-if="isPermissionCanView" fluid>
      <v-row class="mb-1">
        <v-col cols="auto">
          <div class="text-h5 mt-1">นำออกข้อมูลรถโดยสาร</div>
        </v-col>
      </v-row>

      <CustomCardFormViewModern
        title="ตัวกรองข้อมูล"
        :isShowTitle="true"
        :isTitleHighlight="true"
        :setPaddingToCardText="true"
      >
        <!-- ประเภทข้อมูล -->
        <v-row no-gutters class="px-9 pt-5 pb-2">
          <v-col cols="12">
            <InputView title="ประเภทข้อมูล" :isRequired="true">
              <v-radio-group
                v-model="dataType"
                row
                hide-details
                class="mt-1"
                @change="onDataTypeChange"
              >
                <v-radio label="ข้อมูลรถ" value="profile"></v-radio>
                <v-radio label="ประกันภัย" value="insurance"></v-radio>
                <v-radio label="พ.ร.บ." value="act"></v-radio>
                <v-radio label="ภาษี" value="tax"></v-radio>
                <v-radio label="เติมน้ำมัน" value="fuel"></v-radio>
                <v-radio label="บัตรเติมน้ำมัน" value="feedcard"></v-radio>
              </v-radio-group>
            </InputView>
          </v-col>
        </v-row>

        <v-divider class="mx-6"></v-divider>

        <!-- Filters: ข้อมูลรถ -->
        <template v-if="dataType === 'profile'">
          <v-row no-gutters class="px-6 pt-5 pb-4">
            <v-col cols="12" md="4" class="px-3">
              <InputView title="สถานะ">
                <v-select
                  v-model="profileFilter.status"
                  :items="vehicleStatusList"
                  item-text="label"
                  item-value="key"
                  placeholder="เลือกสถานะ"
                  class="app-custom-input custom-placeholer-color"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-select>
              </InputView>
            </v-col>
            <v-col cols="12" md="4" class="px-3">
              <InputView title="ประเภทรถ">
                <v-select
                  v-model="profileFilter.type"
                  :items="vehicleTypeList"
                  item-text="label"
                  item-value="key"
                  placeholder="เลือกประเภทรถ"
                  class="app-custom-input custom-placeholer-color"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-select>
              </InputView>
            </v-col>
            <v-col cols="12" md="4" class="px-3">
              <InputView title="ประเภทป้าย">
                <v-select
                  v-model="profileFilter.licenseType"
                  :items="licenseTypeList"
                  item-text="label"
                  item-value="key"
                  placeholder="เลือกประเภทป้าย"
                  class="app-custom-input custom-placeholer-color"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-select>
              </InputView>
            </v-col>
          </v-row>
        </template>

        <!-- Filters: ประกันภัย / พ.ร.บ. / ภาษี -->
        <template v-if="['insurance', 'act', 'tax'].includes(dataType)">
          <v-row no-gutters class="px-6 pt-5 pb-4">
            <v-col cols="12" md="4" class="px-3">
              <InputView title="ทะเบียนรถ">
                <v-autocomplete
                  v-model="docFilter.vehicleId"
                  :items="vehicleList"
                  item-text="displayLabel"
                  item-value="_id"
                  placeholder="เลือกทะเบียนรถ"
                  class="app-custom-input custom-placeholer-color"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-autocomplete>
              </InputView>
            </v-col>
            <v-col cols="12" md="4" class="px-3">
              <InputView title="วันหมดอายุเริ่มต้น">
                <v-menu
                  v-model="docFilter.expiryStartDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="docFilter.expiryStartDate"
                      placeholder="เลือกวันที่"
                      class="app-custom-input custom-placeholer-color"
                      outlined
                      dense
                      hide-details
                      readonly
                      clearable
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="docFilter.expiryStartDate"
                    @input="docFilter.expiryStartDateMenu = false"
                    locale="th"
                  ></v-date-picker>
                </v-menu>
              </InputView>
            </v-col>
            <v-col cols="12" md="4" class="px-3">
              <InputView title="วันหมดอายุสิ้นสุด">
                <v-menu
                  v-model="docFilter.expiryEndDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="docFilter.expiryEndDate"
                      placeholder="เลือกวันที่"
                      class="app-custom-input custom-placeholer-color"
                      outlined
                      dense
                      hide-details
                      readonly
                      clearable
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="docFilter.expiryEndDate"
                    @input="docFilter.expiryEndDateMenu = false"
                    locale="th"
                  ></v-date-picker>
                </v-menu>
              </InputView>
            </v-col>
          </v-row>
        </template>

        <!-- Filters: เติมน้ำมัน -->
        <template v-if="dataType === 'fuel'">
          <v-row no-gutters class="px-6 pt-5 pb-4">
            <v-col cols="12" md="3" class="px-3">
              <InputView title="ทะเบียนรถ">
                <v-autocomplete
                  v-model="fuelFilter.vehicleId"
                  :items="vehicleList"
                  item-text="displayLabel"
                  item-value="_id"
                  placeholder="เลือกทะเบียนรถ"
                  class="app-custom-input custom-placeholer-color"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-autocomplete>
              </InputView>
            </v-col>
            <v-col cols="12" md="3" class="px-3">
              <InputView title="สถานะ">
                <v-select
                  v-model="fuelFilter.status"
                  :items="fuelStatusList"
                  item-text="label"
                  item-value="key"
                  placeholder="เลือกสถานะ"
                  class="app-custom-input custom-placeholer-color"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-select>
              </InputView>
            </v-col>
            <v-col cols="12" md="3" class="px-3">
              <InputView title="วันที่เริ่มต้น">
                <v-menu
                  v-model="fuelFilter.startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="fuelFilter.startDate"
                      placeholder="เลือกวันที่"
                      class="app-custom-input custom-placeholer-color"
                      outlined
                      dense
                      hide-details
                      readonly
                      clearable
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="fuelFilter.startDate"
                    @input="fuelFilter.startDateMenu = false"
                    locale="th"
                  ></v-date-picker>
                </v-menu>
              </InputView>
            </v-col>
            <v-col cols="12" md="3" class="px-3">
              <InputView title="วันที่สิ้นสุด">
                <v-menu
                  v-model="fuelFilter.endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="fuelFilter.endDate"
                      placeholder="เลือกวันที่"
                      class="app-custom-input custom-placeholer-color"
                      outlined
                      dense
                      hide-details
                      readonly
                      clearable
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="fuelFilter.endDate"
                    @input="fuelFilter.endDateMenu = false"
                    locale="th"
                  ></v-date-picker>
                </v-menu>
              </InputView>
            </v-col>
          </v-row>
        </template>

        <!-- Filters: บัตรเติมน้ำมัน -->
        <template v-if="dataType === 'feedcard'">
          <v-row no-gutters class="px-6 pt-5 pb-4">
            <v-col cols="12" md="4" class="px-3">
              <InputView title="ทะเบียนรถ">
                <v-autocomplete
                  v-model="feedcardFilter.vehicleId"
                  :items="vehicleList"
                  item-text="displayLabel"
                  item-value="_id"
                  placeholder="เลือกทะเบียนรถ"
                  class="app-custom-input custom-placeholer-color"
                  outlined
                  dense
                  hide-details
                  clearable
                ></v-autocomplete>
              </InputView>
            </v-col>
          </v-row>
        </template>

        <div class="px-9 pb-3">
          <span class="red--text text-app-small"
            >* ถ้าว่างไว้หมายถึงเงื่อนไขทั้งหมด</span
          >
        </div>
        <v-divider></v-divider>
        <v-row no-gutters class="px-9 py-4" align="center">
          <v-col cols="auto" v-if="dataType === 'profile'">
            <InputView title="รูปแบบการนำออก" :isRequired="true">
              <v-radio-group
                v-model="profileFilter.exportType"
                row
                hide-details
                class="mt-1"
              >
                <v-radio label="แบบย่อ" value="short"></v-radio>
                <v-radio label="แบบเต็ม" value="full"></v-radio>
              </v-radio-group>
            </InputView>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto" :class="dataType === 'profile' ? 'pt-7' : ''">
            <PrimaryButton
              icon="mdi-download"
              :loading="exportLoading"
              @on-click="exportCSV"
            >
              นำออก CSV
            </PrimaryButton>
          </v-col>
        </v-row>
      </CustomCardFormViewModern>
    </v-container>
    <AppPageNotFound :show="isPermissionCanView" />

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import AppPageNotFound from "@/components/AppPageNotFound.vue";
import CustomCardFormViewModern from "@/components/CustomCardFormViewModern.vue";
import InputView from "@/components/InputView.vue";
import PrimaryButton from "@/components/PrimaryButton.vue";
import {
  getExportVehicles,
  getExportVehicleTypes,
  getExportVehicle,
  getExportVehicleInsurance,
  getExportVehicleAct,
  getExportVehicleTax,
  getExportVehicleFuel,
  getExportVehicleFeedcard,
} from "@/services/api/export_data";
import moment from "moment";

export default {
  name: "ExportVehicleView",
  components: {
    AppPageNotFound,
    CustomCardFormViewModern,
    InputView,
    PrimaryButton,
  },
  data() {
    return {
      isPermissionCanView: false,
      exportLoading: false,
      dataType: "profile",
      //
      vehicleList: [],
      vehicleTypeList: [],
      vehicleStatusList: [
        { key: "ACTIVE", label: "พร้อม" },
        { key: "INACTIVE", label: "ไม่พร้อม" },
        { key: "SALED", label: "ขายแล้ว" },
      ],
      licenseTypeList: [
        { key: "YELLOW", label: "เหลือง" },
        { key: "WHITE", label: "ขาว" },
        { key: "RED", label: "แดง" },
      ],
      fuelStatusList: [
        { key: "A", label: "อนุมัติ" },
        { key: "P", label: "รอตรวจ" },
        { key: "R", label: "ปฏิเสธ" },
      ],
      //
      profileFilter: {
        status: "ACTIVE",
        type: null,
        licenseType: null,
        exportType: "short",
      },
      docFilter: {
        vehicleId: null,
        expiryStartDate: null,
        expiryEndDate: null,
        expiryStartDateMenu: false,
        expiryEndDateMenu: false,
      },
      fuelFilter: {
        vehicleId: null,
        status: null,
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        startDateMenu: false,
        endDateMenu: false,
      },
      feedcardFilter: {
        vehicleId: null,
      },
      //
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
    };
  },
  mounted() {
    this.checkUserPermissions();
    if (this.isPermissionCanView) {
      this.loadFilterOptions();
    }
  },
  methods: {
    checkUserPermissions() {
      if (this.$store.getters.userInfo !== null) {
        let permissions = this.$store.getters.userInfo.permissions;
        if (
          permissions &&
          (permissions.includes("ADMIN_ALL") ||
            permissions.includes("EXPORT_DATA_VIEW"))
        ) {
          this.isPermissionCanView = true;
        }
      }
    },

    async loadFilterOptions() {
      const [vehicles, vehicleTypes] = await Promise.all([
        getExportVehicles(this.$store),
        getExportVehicleTypes(this.$store),
      ]);

      if (vehicles.code === 200 && vehicles.data) {
        this.vehicleList = vehicles.data;
      }
      if (vehicleTypes.code === 200 && vehicleTypes.data) {
        this.vehicleTypeList = vehicleTypes.data;
      }
    },

    onDataTypeChange() {},

    async exportCSV() {
      this.exportLoading = true;
      try {
        let resp;
        let filename;
        const nameMap = {
          profile: "ข้อมูลรถโดยสาร",
          insurance: "ประกันภัย",
          act: "พรบ",
          tax: "ภาษี",
          fuel: "เติมน้ำมัน",
          feedcard: "บัตรเติมน้ำมัน",
        };

        switch (this.dataType) {
          case "profile":
            resp = await getExportVehicle(this.$store, this.profileFilter);
            filename = `${nameMap.profile}_${moment().format("YYYY-MM-DD")}`;
            break;

          case "insurance":
            resp = await getExportVehicleInsurance(this.$store, this.docFilter);
            filename = `${nameMap.insurance}_${moment().format("YYYY-MM-DD")}`;
            break;

          case "act":
            resp = await getExportVehicleAct(this.$store, this.docFilter);
            filename = `${nameMap.act}_${moment().format("YYYY-MM-DD")}`;
            break;

          case "tax":
            resp = await getExportVehicleTax(this.$store, this.docFilter);
            filename = `${nameMap.tax}_${moment().format("YYYY-MM-DD")}`;
            break;

          case "fuel":
            resp = await getExportVehicleFuel(this.$store, this.fuelFilter);
            filename = `${nameMap.fuel}_${this.fuelFilter.startDate || "ทั้งหมด"}_${this.fuelFilter.endDate || "ทั้งหมด"}`;
            break;

          case "feedcard":
            resp = await getExportVehicleFeedcard(
              this.$store,
              this.feedcardFilter
            );
            filename = `${nameMap.feedcard}_${moment().format("YYYY-MM-DD")}`;
            break;
        }

        if (resp && resp.code === 200 && resp.data) {
          const records = resp.data.records;
          if (records.length === 0) {
            this.showSnackbar("ไม่มีข้อมูลสำหรับนำออก", "warning");
            this.exportLoading = false;
            return;
          }

          const headers = Object.keys(records[0]);
          const csvRows = [headers.join(",")];
          for (const row of records) {
            const values = headers.map((h) => {
              let val = row[h];
              if (val === null || val === undefined) return "";
              val = String(val).replace(/"/g, '""');
              return `"${val}"`;
            });
            csvRows.push(values.join(","));
          }
          const csvContent = csvRows.join("\n");
          const blob = new Blob(["\uFEFF" + csvContent], {
            type: "text/csv;charset=utf-8;",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${filename}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          this.showSnackbar(
            `นำออกสำเร็จ ${records.length} รายการ`,
            "success"
          );
        }
      } catch (err) {
        this.showSnackbar("ไม่สามารถนำออกข้อมูลได้", "error");
      }
      this.exportLoading = false;
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>
